<!-- src/views/odontogram/OdontogramDetail.vue -->
<template>
  <div class="odontogram-detail">
    <!-- Back button and page title -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <router-link :to="returnPath" class="btn btn-outline-primary">
        <i class="bi bi-arrow-left me-2"></i>Volver
      </router-link>
      <h1>Odontograma</h1>
      <div>
        <button
          v-if="!isEditMode"
          class="btn btn-primary"
          @click="enableEditMode"
        >
          <i class="bi bi-pencil me-1"></i> Editar
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ error }}
    </div>

    <!-- Odontogram content -->
    <div v-else class="card">
      <div class="card-header">
        <h5 class="mb-0">
          Odontograma de {{ patientName }}
          <small class="text-muted" v-if="lastUpdated">
            (Última actualización: {{ lastUpdated }})
          </small>
        </h5>
      </div>
      <div class="card-body">
        <!-- Main odontogram component -->
        <Odontogram 
          :odontogramId="odontogramId" 
          :isEditable="isEditMode" 
          @updated="onOdontogramUpdated"
        />
        
        <!-- Observaciones section -->
        <div class="mt-4">
          <h5 class="mb-3">Observaciones</h5>
          <div v-if="isEditMode" class="mb-3">
            <textarea 
              class="form-control" 
              v-model="observationsText" 
              rows="4" 
              placeholder="Ingrese observaciones generales sobre el odontograma">
            </textarea>
          </div>
          <div v-else>
            <div v-if="observationsText" class="odontogram-observations p-3 bg-light rounded">
              <div class="field-content">
                <p v-for="(line, index) in getObservationLines(observationsText)" 
                   :key="index" 
                   class="mb-1">
                  {{ line }}
                </p>
              </div>
            </div>
            <p v-else class="text-muted fst-italic">No hay observaciones registradas</p>
          </div>
        </div>
      </div>
      <div class="card-footer d-flex justify-content-between">
        <span class="text-muted">Haga clic en un diente para ver o editar su estado</span>
        <div v-if="isEditMode">
          <button class="btn btn-outline-secondary me-2" @click="cancelEdit">
            Cancelar
          </button>
          <button class="btn btn-primary" @click="saveChanges">
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Odontogram from '@/components/odontogram/Odontogram.vue';
import { odontogramService, patientService, clinicalHistoryService } from '@/services/api';

export default {
  name: 'OdontogramDetail',
  components: {
    Odontogram
  },
  data() {
    return {
      loading: true,
      error: null,
      odontogram: null,
      patient: null,
      clinicalHistory: null,
      isEditMode: false,
      lastUpdated: null,
      observationsText: ''
    };
  },
  computed: {
    odontogramId() {
      return this.$route.params.id;
    },
    patientName() {
      if (this.patient) {
        return `${this.patient.first_name} ${this.patient.last_name}`;
      }
      return 'Paciente';
    },
    returnPath() {
      if (this.patient) {
        return `/patients/${this.patient.id}`;
      }
      return '/';
    }
  },
  async created() {
    try {
      await this.fetchOdontogramData();
    } catch (error) {
      console.error('Error loading odontogram:', error);
      this.error = 'No se pudo cargar el odontograma. Por favor, intenta de nuevo.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    // Split observations text into lines for line-by-line display
    getObservationLines(text) {
      if (!text) return ['No hay observaciones registradas'];
      
      // Split by newlines or create an array with a single item if no newlines
      return text.split(/\r?\n/).filter(line => line.trim() !== '');
    },
    
    async fetchOdontogramData() {
      // Fetch odontogram data
      const odontogramResponse = await odontogramService.get(this.odontogramId);
      this.odontogram = odontogramResponse.data;
      
      // Set observations text
      this.observationsText = this.odontogram.data || '';
      
      // Get clinical history and then patient
      const clinicalHistoryId = this.odontogram.clinical_history;
      const clinicalHistoryResponse = await clinicalHistoryService.get(clinicalHistoryId);
      this.clinicalHistory = clinicalHistoryResponse.data;
      
      const patientId = this.clinicalHistory.patient;
      const patientResponse = await patientService.get(patientId);
      this.patient = patientResponse.data;
      
      // Set last updated date if available
      if (this.odontogram.updated_at) {
        this.lastUpdated = new Date(this.odontogram.updated_at).toLocaleString();
      }
    },
    enableEditMode() {
      this.isEditMode = true;
    },
    cancelEdit() {
      this.isEditMode = false;
      // Refresh data to discard changes
      this.fetchOdontogramData();
    },
    async saveChanges() {
      try {
        // Save the observations text
        await odontogramService.update(this.odontogramId, {
          ...this.odontogram,
          data: this.observationsText
        });
        
        // Refresh data
        await this.fetchOdontogramData();
        
        // Exit edit mode
        this.isEditMode = false;
      } catch (error) {
        console.error('Error saving odontogram observations:', error);
        alert('No se pudieron guardar las observaciones. Por favor, intenta de nuevo.');
      }
    },
    onOdontogramUpdated() {
      // Update the last updated timestamp
      this.lastUpdated = new Date().toLocaleString();
    }
  }
};
</script>

<style scoped>
.odontogram-observations {
  background-color: #f8f9fa;
  border-left: 3px solid #0d6efd;
}

.field-content p {
  margin-bottom: 0.25rem;
  line-height: 1.5;
}

.field-content p:last-child {
  margin-bottom: 0;
}
</style>